import { createSignal, Show } from "solid-js";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "@solidjs/router";
import toast from "solid-toast";
import { useProfile } from "../contexts/ProfileContext";

export default function LoginPage() {
  const [email, setEmail] = createSignal("");
  const [password, setPassword] = createSignal("");
  const [showPassword, setShowPassword] = createSignal(false);
  const [isSubmitting, setIsSubmitting] = createSignal(false);

  const auth = useAuth();
  const navigate = useNavigate();
  const { clearProfile, profile, profileConfig } = useProfile();

  const handleSubmit = async (e: Event) => {
    e.preventDefault();

    if (!email().trim() || !password().trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const success = await auth.login(email().trim(), password());

      if (success) {
        toast.success(`Welcome back, ${auth.user()?.name}!`);
        // Redirect based on role
        if (auth.user()?.role === "template_admin") {
          navigate("/admin/questions");
        } else {
          navigate("/sessions");
        }
      } else {
        toast.error("Invalid email or password");
      }
    } catch (error) {
      toast.error("Login failed. Please try again.");
      console.error("Login error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div class="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
      <div class="max-w-md w-full space-y-8">
        <div class="text-center">
          <h1 class="text-4xl font-bold text-blue-600 mb-2">IntelliCoach</h1>
          <h2 class="text-2xl font-semibold text-gray-900 mb-2">
            Welcome Back
          </h2>
        </div>

        <div class="bg-white rounded-lg shadow-md p-8">
          <Show when={profile()}>
            <p class="text-sm text-gray-500 mb-6">
              Client profile:{" "}
              <span class="font-semibold text-gray-700">
                {profileConfig()?.name || profile()}
              </span>
            </p>
          </Show>

          <form onSubmit={handleSubmit} class="space-y-6">
            <div>
              <label
                for="email"
                class="block text-sm font-medium text-gray-700 mb-2"
              >
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email()}
                onInput={(e) => setEmail(e.currentTarget.value)}
                class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your email"
                disabled={isSubmitting()}
                required
              />
            </div>

            {/* Password Field */}
            <div>
              <label
                for="password"
                class="block text-sm font-medium text-gray-700 mb-2"
              >
                Password
              </label>
              <div class="relative">
                <input
                  id="password"
                  type={showPassword() ? "text" : "password"}
                  value={password()}
                  onInput={(e) => setPassword(e.currentTarget.value)}
                  class="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter your password"
                  disabled={isSubmitting()}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword())}
                  class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  disabled={isSubmitting()}
                >
                  <Show
                    when={showPassword()}
                    fallback={
                      <svg
                        class="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    }
                  >
                    <svg
                      class="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                      />
                    </svg>
                  </Show>
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting()}
              class={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white transition-colors ${
                isSubmitting()
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              }`}
            >
              <Show when={isSubmitting()} fallback="Sign In">
                <svg
                  class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  ></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Signing In...
              </Show>
            </button>
          </form>

          {/* Demo Credentials */}
          {/* <div class="mt-6 p-4 bg-gray-50 rounded-md">
            <h3 class="text-sm font-medium text-gray-700 mb-2">
              Demo Credentials:
            </h3>
            <div class="text-xs text-gray-600 space-y-1">
              <div>
                <strong>Admin:</strong> admin@coachp.com / admin123
              </div>
              <div>
                <strong>User:</strong> user@coachp.com / user123
              </div>
              <div>
                <strong>Demo:</strong> demo@coachp.com / demo123
              </div>
            </div>
          </div> */}
        </div>

        {/* Footer */}
        <div class="text-center text-sm text-gray-500">
          <p>This is a demo environment.</p>
          <Show when={profile()}>
            <button
              type="button"
              class="mt-3 text-blue-600 hover:text-blue-700 font-medium"
              onClick={() => {
                clearProfile();
                navigate("/", { replace: true });
              }}
            >
              Change client
            </button>
          </Show>
        </div>
      </div>
    </div>
  );
}
